import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { useAlleNachrichten } from '../../hooks/buch'
import { useHighlights, useMembers, useTracks, useTrip } from '../../hooks/queries'
import { ausschnittAus, bildStil } from '../../lib/ausschnitt'
import { Buchkarte } from './Buchkarte'
import { useBuchquelle } from './buchquelle'
import { imZeitraum, istGesetzt, zeitraumAus, zeitraumText } from './zeitraum'
import { Beitrag, Punktseite } from './Buchteile'
import { ExportButtons } from './ExportButtons'
import { MedienExport } from './MedienExport'
import { tripPeriod } from '../../lib/format'
import { buchTag, datumText } from '../../lib/ortszeit'
import { BackIcon } from '../../components/Icons'
import { useTexte } from '../../texte'
import '../../styles/buch.css'

/**
 * Die Reise als Buch (#14).
 *
 * **Antworten stehen nicht darin, und das entscheidet nicht diese Seite.**
 * `Visibility::scope()` gibt dem Absender ohne `?with=` ausschließlich
 * `kind = 'broadcast'` zurück — hier wird deshalb nichts gefiltert. Die eine
 * Regel bleibt an der einen Stelle; ein Filter hier wäre eine zweite, und die
 * zweite läuft irgendwann auseinander.
 *
 * **The server builds the file** (since 0.30.0): PDF or EPUB, with photos,
 * videos and the maps from this page. The page stays as the preview -- and
 * as the place where the maps are drawn that the server cannot draw.
 * Printing still works via the browser's menu; the rules for it are in
 * `styles/buch.css`.
 */
export function BuchPage() {
  const { tripId } = useParams()
  const id = Number(tripId)
  const [suche] = useSearchParams()

  const trip = useTrip(id)
  const punkte = useHighlights(id, true)
  const spuren = useTracks(id, true)
  const empfaenger = useMembers(id, true)
  const stand = useAlleNachrichten(id)

  /**
   * **Welche Karten schon fertig sind — als Menge und nicht als Zahl.**
   *
   * Es ist nicht mehr eine Karte, sondern die Übersicht **und** ein kleiner
   * Ausschnitt an jedem Punkt. Ein Zähler wäre die naheliegende Lösung und die
   * falsche: meldete eine Karte zweimal (ein Neuzeichnen genügt), stünde der
   * Knopf offen, während eine andere noch grau ist. Eine Menge zählt jede nur
   * einmal, und zwar die richtige.
   */
  const [fertig, setFertig] = useState<ReadonlySet<string>>(new Set())
  const melde = useCallback((was: string) => {
    setFertig((alt) => (alt.has(was) ? alt : new Set(alt).add(was)))
  }, [])

  const versatz = trip.data?.tz_offset_min ?? null

  /**
   * Der Zeitraum schneidet zu — und er schneidet **alles** zu, nicht nur die
   * Beiträge. Ein Buch, in dem die Karte die ganze Reise zeigt und der Text
   * eine Woche, wäre zwei Bücher in einem.
   *
   * Die Rechnung selbst steht in `zeitraum.ts`, weil das private Dokument und
   * die Infoseite dieselbe brauchen. Sie hing hier als `useMemo` und galt
   * damit nur für diese Seite.
   */
  const zeitraum = useMemo(() => zeitraumAus(suche), [suche])
  const passt = useMemo(() => imZeitraum(zeitraum, versatz), [zeitraum, versatz])
  const t = useTexte().buch

  const beitraege = stand.nachrichten.filter((m) => passt(m.created_at))
  const gezeigtePunkte = (punkte.data?.highlights ?? [])
    .filter((p) => p.published_at !== null)
    .filter((p) => passt(p.happened_at))

  // Der Titel des Fensters wird im Druckdialog zum Dateinamen -- deshalb steht
  // dort der Name der Reise und nicht „Travel Broadcast".
  useEffect(() => {
    const alt = document.title
    if (trip.data !== undefined) document.title = trip.data.title
    return () => {
      document.title = alt
    }
  }, [trip.data])

  const quelle = useBuchquelle()

  /*
   * **Gewartet wird auf die, die wirklich dastehen.** Die Übersichtskarte gibt
   * es nur, wenn es Punkte oder eine Route gibt; einen Ausschnitt gibt es je
   * gezeigtem Punkt. Wer auf eine Karte wartet, die gar nicht gezeichnet wird,
   * lässt den Druckknopf für immer zu.
   */
  const zeigtRoute = gezeigtePunkte.length > 0 || (spuren.data?.tracks?.length ?? 0) > 0
  const noetig = [
    ...(zeigtRoute ? ['route'] : []),
    ...gezeigtePunkte.map((p) => `punkt:${p.id}`),
  ]
  const bereit = !stand.laeuft && noetig.every((k) => fertig.has(k))

  return (
    /*
     * **Die Leiste steht neben dem Scrollkasten, nicht darin.**
     *
     * Sie war ein `position: sticky` innerhalb von `.buch` -- und damit im
     * Kasten, den sie überdeckt. Auf iOS ruckelt so eine Kopfzeile im
     * Schwungscrollen, und weil `.buch` einen Innenabstand hat, scrollte der
     * Inhalt links und rechts sichtbar an ihr vorbei. Gemeldet als „komische
     * Effekte" beim Scrollen. Jetzt sind es zwei Geschwister in einer
     * Flex-Spalte -- so wie überall sonst in dieser Anwendung.
     */
    <div className="buchseite">
      <div className="buch__leiste buch__nurbildschirm">
        <Link className="icon-button" to={`/t/${id}/info`} aria-label={t.zurueck}>
          <BackIcon />
        </Link>
        <span className="bar__grow">
          {stand.laeuft
            ? t.zusammenstellen(stand.seiten)
            : t.umfang(beitraege.length, gezeigtePunkte.length)}
        </span>
        {stand.fehler !== null && (
          <p className="notice" role="alert" style={{ margin: 0 }}>
            {stand.fehler}
          </p>
        )}
        {/* **The server builds the file** (#14, since 0.30.0). The maps here are
            drawn for it: the export copies them and sends them along. */}
        <ExportButtons
          tripId={id}
          from={zeitraum.von}
          to={zeitraum.bis}
          ready={bereit && stand.fehler === null}
          waitingText={stand.laeuft ? t.nochNichtFertig : t.nichtBereit}
        />
      </div>
      {/* The media as a ZIP (#75): its own row under the bar, with the
          sentence about the places -- the bar is full at 390 points. */}
      <div className="buch__leiste buch__leiste--medien buch__nurbildschirm">
        <MedienExport tripId={id} />
      </div>

      {/* **Die eigene Fußzeile.** Sie wiederholt sich beim Drucken auf jeder
          Seite; Safaris eigene Zeile mit Adresse und Seitenzahl steht daneben
          im Seitenrand und lässt sich nur im Druckdialog abschalten. */}
      <p className="buch__fuss">{trip.data?.title ?? t.reise}</p>

      <div className="buch">
        <section className="buch__deckel">
          {trip.data?.cover_url != null && (
            <span className="buch__deckelbild">
              <img
                src={trip.data.cover_url}
                alt=""
                style={bildStil(ausschnittAus(trip.data.cover_crop))}
              />
            </span>
          )}
          <h1>{trip.data?.title ?? t.reise}</h1>
          <p>{tripPeriod(trip.data?.starts_on ?? null, trip.data?.ends_on ?? null)}</p>
          <p>{trip.data?.owner.display_name}</p>
          {/* **Ein Ausschnitt sagt, dass er einer ist.** Sonst hält das Buch
              in ein paar Jahren jemand für den ganzen Bericht und sucht
              vergeblich nach dem Rest. */}
          {istGesetzt(zeitraum) && (
            <p className="buch__ausschnitt">
              <strong>{t.ausschnitt(zeitraumText(zeitraum))}</strong>
            </p>
          )}
        </section>

        {(gezeigtePunkte.length > 0 || (spuren.data?.tracks?.length ?? 0) > 0) && (
          <section className="buch__abschnitt">
            <h2>{t.route}</h2>
            <Buchkarte
              tracks={spuren.data?.tracks ?? []}
              punkte={gezeigtePunkte}
              onFertig={() => melde('route')}
            />
          </section>
        )}

        <section className="buch__abschnitt">
          <h2>{t.bericht}</h2>
          {/* **Ohne Zeitraum ist „in diesem Zeitraum" die falsche Auskunft.**
              Der Satz stand fest da und behauptete einen Ausschnitt, den
              niemand gewählt hatte. */}
          {beitraege.length === 0 && (
            <p>
              {istGesetzt(zeitraum) ? t.keinBeitragImZeitraum : t.keinBeitrag}
            </p>
          )}
          {beitraege.map((nachricht, i) => {
            const vorher = beitraege[i - 1]
            /* **Ein Buch kennt kein „Heute".** `dayLabel` sagt das und rechnet
               die Tagesgrenze in der Uhr des Geräts -- richtig für die
               Chatliste, falsch für Papier. Angezeigt und verglichen wird mit
               derselben Funktion; zwei wären zwei Regeln. */
            const neuerTag =
              vorher === undefined
              || buchTag(vorher.created_at, versatz) !== buchTag(nachricht.created_at, versatz)
            return (
              <div key={nachricht.id}>
                {neuerTag && (
                  <p className="buch__tag">{buchTag(nachricht.created_at, versatz)}</p>
                )}
                <Beitrag nachricht={nachricht} versatz={versatz} />
              </div>
            )
          })}
        </section>

        {gezeigtePunkte.length > 0 && (
          <section className="buch__abschnitt">
            <h2>{t.punkte}</h2>
            {/* **Der Lizenzvermerk für die kleinen Ausschnitte — einmal, und
                nicht von Hand geschrieben.** Er kommt aus derselben festen
                Liste, aus der Leaflet ihn an der Übersichtskarte bezieht
                (`MapSources`); wer die Quelle wechselt, wechselt ihn
                zwangsläufig mit. Als HTML, weil er eines ist (`&copy;`) — und
                es ist kein frei eingetippter Text, sondern einer von zwei
                festen Einträgen. */}
            <p
              className="buch__quelle buch__punkt-zeile"
              dangerouslySetInnerHTML={{ __html: t.kartenausschnitte(quelle.attribution) }}
            />
            {gezeigtePunkte.map((punkt, i) => (
              <Punktseite
                key={punkt.id}
                punkt={punkt}
                nummer={i + 1}
                reiseVersatz={versatz}
                onKarteFertig={() => melde(`punkt:${punkt.id}`)}
              />
            ))}
          </section>
        )}

        <section className="buch__abschnitt">
          {/* **Der Stand des Exporttags, und die Seite sagt es.** Wer wann dabei
              war, ließe sich aus `join_requests` rekonstruieren -- das wäre ein
              eigenes Vorhaben und für ein Buch die falsche Genauigkeit. */}
          <h2>{t.empfaenger}</h2>
          <p className="buch__stand">{t.stand(datumText(new Date().toISOString(), versatz))}</p>
          <ul className="buch__liste">
            {(empfaenger.data ?? [])
              .filter((m) => m.role === 'recipient' && m.left !== true)
              .map((m) => (
                <li key={m.user.id}>{m.user.display_name}</li>
              ))}
          </ul>
        </section>
      </div>
    </div>
  )
}
